/**
 * CR-8611: which date ranges each Cost sub-tab accepts.
 *
 * The Cost tab's two sub-tabs look in opposite directions: Future is what is
 * still to come in, Historical is goods already received. A range that suits
 * one gives the other little or nothing (Historical on Next 20 weeks showed a
 * Historical Spend of 0), so each sub-tab only offers its own ranges.
 *
 * - Historical: the "Last" ranges — they end before today.
 * - Future: the "This/Next" ranges — they end today or later ("This" ranges
 *   run to the end of the current month).
 *
 * The same end-date rule judges a custom From/To range, so a typed range
 * cannot slip past the buttons. A range that does not fit the sub-tab moves
 * to that sub-tab's default.
 *
 * Framework-free and unit-tested (see __tests__/costViewRanges.test.ts).
 */
import type { DashboardFilterState } from './types';
import { computeQuickDateRange, formatLocalDate } from './dashboardFilterUtils';

export type CostView = 'future' | 'historical';

/** Quick ranges each sub-tab offers, in the ticket's order. */
export const COST_VIEW_RANGES: Record<CostView, string[]> = {
  future: ['next20Weeks', 'thisMonth', 'thisYear'],
  historical: ['last12Months', 'lastMonth', 'lastQuarter', 'lastYear'],
};

/** The range a sub-tab moves to when the current one does not fit it. */
export const COST_VIEW_DEFAULT: Record<CostView, string> = {
  future: 'next20Weeks',
  historical: 'last12Months',
};

/** Why a range is disabled — shown as its tooltip. */
export const COST_VIEW_DISABLED_HINT: Record<CostView, string> = {
  future: 'Future shows what is still to come in — choose a current or upcoming range',
  historical: 'Historical shows goods already received — choose a past range',
};

/**
 * Whether the selected dates suit the sub-tab.
 *
 * @param view - the Cost sub-tab
 * @param filters - current dashboard dates
 * @param now - today (for tests)
 * @returns true when the range ends before today (Historical) / today or later (Future)
 */
export function rangeFitsCostView(
  view: CostView,
  filters: Pick<DashboardFilterState, 'dateFrom' | 'dateTo'>,
  now: Date = new Date(),
): boolean {
  // CR-1421 decision 2: "All future weeks" has a start and no end — forward by definition.
  if (!filters.dateTo) return view === 'future' && !!filters.dateFrom;
  const today = formatLocalDate(now);
  return view === 'historical' ? filters.dateTo < today : filters.dateTo >= today;
}

/**
 * The filters to apply for the sub-tab: unchanged when the range fits it,
 * otherwise the sub-tab's default range (dates and Historical/Live mode).
 *
 * @param view - the Cost sub-tab
 * @param filters - current dashboard filter state
 * @param now - today (for tests)
 * @returns the new state, or null when nothing needs to change
 */
export function costViewFilters(
  view: CostView,
  filters: DashboardFilterState,
  now: Date = new Date(),
): DashboardFilterState | null {
  if (rangeFitsCostView(view, filters, now)) return null;
  const range = computeQuickDateRange(COST_VIEW_DEFAULT[view]);
  if (!range) return null;
  return { ...filters, dateFrom: range.dateFrom, dateTo: range.dateTo, dataMode: range.dataMode };
}

/**
 * Limits for the custom To-date picker on the sub-tab.
 *
 * @param view - the Cost sub-tab
 * @param now - today (for tests)
 * @returns `max` (Historical: yesterday) or `min` (Future: today)
 */
export function costViewToDateBounds(view: CostView, now: Date = new Date()): { min?: string; max?: string } {
  if (view === 'future') return { min: formatLocalDate(now) };
  const yesterday = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
  return { max: formatLocalDate(yesterday) };
}

/**
 * Whether a stored value is a Cost sub-tab.
 *
 * @param value - anything read back from storage
 * @returns the value as a CostView, or null
 */
export function asCostView(value: unknown): CostView | null {
  return value === 'future' || value === 'historical' ? value : null;
}

const RANGE_LABELS: Record<string, string> = {
  next20Weeks: 'Next 20 weeks',
  thisMonth: 'This month',
  thisYear: 'This year',
  last12Months: 'Last 12 months',
  lastMonth: 'Last month',
  lastQuarter: 'Last quarter',
  lastYear: 'Last year',
};

/**
 * The quick range the dates match, as a label ("Last quarter").
 *
 * @param filters - current dashboard dates
 * @returns the label, or null for a custom range
 */
export function quickRangeLabel(filters: Pick<DashboardFilterState, 'dateFrom' | 'dateTo'>): string | null {
  const key = Object.keys(RANGE_LABELS).find((k) => {
    const dates = computeQuickDateRange(k);
    return dates && dates.dateFrom === filters.dateFrom && dates.dateTo === filters.dateTo;
  });
  return key ? RANGE_LABELS[key] ?? null : null;
}
